import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CFormFeedback, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const Partnership = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [toast, setToast] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_manager = decodedToken.is_manager;

    const fetchPartnershipDetails = async () => {
        let data = null; // Declare a variable to store API response
        try {
            const response = await apiClient.get(`/partnerships/${id}/`);
            data = response.data; // Store response data
            setFormData(data); // Update state
        } catch (error) {
            triggerAlert('Parceria não encontrada', 'error');
            navigate("/parcerias");
        }
    };

    useEffect(() => {
        if (!id) {
            setFormData({});
        } else {
            fetchPartnershipDetails();
        }
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
    
        let updatedValue;
        if (type === "checkbox") {
            updatedValue = checked;
        } else if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else if (value === "") {
            updatedValue = null;
        } else {
            updatedValue = value;
        }
    
        setFormData((prevFormData) => {
            const updatedFormData = {
                ...prevFormData,
                [name]: updatedValue,
            };
    
            return updatedFormData;
        });
    
        setFieldErrors((prevErrors) => {
            const updated = { ...prevErrors };
            delete updated[name];
            return updated;
        });    
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (id) {
                await apiClient.put(`/partnerships/${id}/`, formData);
                triggerAlert('Parceria atualizada com sucesso!', 'success');
                navigate(`/parcerias`);
            } else {
                await apiClient.post('/partnerships/', formData);
                triggerAlert('Parceria criada com sucesso!', 'success');
                navigate(`/parcerias`);
            }
        } catch (error) {
            const errors = error.response?.data;
            let errorMessage = 'Erro ao gravar';

            if (errors && typeof errors === 'object') {
                setFieldErrors(errors); // <-- Save the full error object
            } else {
                setFieldErrors({});
            }

            setTimeout(() => {
                const firstInvalidInput = document.querySelector('input.is-invalid, textarea.is-invalid, select.is-invalid');
                if (firstInvalidInput) {
                    firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    firstInvalidInput.focus();
                }
            }, 50);


            setToast({ title: 'Parceria', message: errorMessage, type: 'error' });
            console.log('Error submitting form:', error);
            setLoading(false);
        }
    };

    if (formData !== null) {
        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification toast={toast} setToast={setToast} />

                <CCard>
                    <CCardHeader>
                        <h4>Fornecedor</h4>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Nome</strong>}
                                    name="name"
                                    value={formData.name ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.name}
                                />
                                {fieldErrors.name && (
                                    <CFormFeedback invalid>{fieldErrors.name[0]}</CFormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Âmbito</strong>}
                                    name="scope"
                                    value={formData.scope ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.scope}
                                />
                                {fieldErrors.scope && (
                                    <CFormFeedback invalid>{fieldErrors.scope[0]}</CFormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Email</strong>}
                                    name="email"
                                    value={formData.email ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.email}
                                />
                                {fieldErrors.email && (
                                    <CFormFeedback invalid>{fieldErrors.email[0]}</CFormFeedback>
                                )}
                            </div>

                            <DateInput
                                label={<strong>Data de Início</strong>}
                                name="start_date"
                                value={formData.start_date ?? ''}
                                onChange={handleInputChange}
                            />

                            <DateInput
                                label={<strong>Data de Fim</strong>}
                                name="end_date"
                                value={formData.end_date ?? ''}
                                onChange={handleInputChange}
                            />

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Tipo de parceria</strong>}
                                    name="partnership_type"
                                    value={formData.partnership_type ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.partnership_type}
                                >
                                    <option value="">Selecione</option>
                                    <option value="Empresa">Empresa</option>
                                    <option value="ENESII">ENESII</option>
                                    <option value="Individual">Individual</option>
                                </CFormSelect>
                                {fieldErrors.partnership_type && (
                                    <CFormFeedback invalid>{fieldErrors.partnership_type[0]}</CFormFeedback>
                                )}
                            </div>

                            <TextEditor
                                label="Objetivos"
                                value={formData.objectives ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "objectives", value } })
                                }
                                className="mb-3"
                           />

                           <CFormSelect
                                label={<strong>Estado</strong>}
                                name="active"
                                value={formData.active ?? ''}
                                onChange={handleInputChange}
                                className="mb-3"
                            >
                                <option value="">Selecione</option>
                                <option value="true">Ativa</option>
                                <option value="false">Inativa</option>
                            </CFormSelect>

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Eficácia</strong>}
                                    name="efficiency"
                                    value={formData.efficiency ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.efficiency}
                                >
                                    <option value="">Selecione</option>
                                    <option value="Eficaz">Eficaz</option>
                                    <option value="Não Eficaz">Não Eficaz</option>
                                </CFormSelect>
                                {fieldErrors.efficiency && (
                                    <CFormFeedback invalid>{fieldErrors.efficiency[0]}</CFormFeedback>
                                )}
                            </div>
                        </CForm>
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate('/parcerias')}>
                            Cancelar
                        </CButton>
                        <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <h2>Loading...</h2>
        </div>
    );
};

export default Partnership;
